<template>
  <div class="bx-header__search bx-typo--header-search">
    <button ref="headerSearchOpener"
            aria-label="Suche öffnen"
            class="bx-header__search-toggle"
            @click="toggleSearch()"
            @keyup.enter="toggleSearch()">
      <Icon name="search" />
    </button>

    <form ref="headerSearchForm"
          class="bx-form bx-form--search-header bx-typo--form-search"
          onsubmit="return false;"
          @submit="submitSearch()">
      <input id="bx-header-search-input"
             ref="searchInput"
             v-model="searchPhrase"
             type="search"
             class="bx-form__input"
             :title="placeholder"
             :placeholder="placeholder"
             :autocomplete="isEpgSearch ? 'off' : 'on'"
             @keyup.enter.prevent="submitSearch()"
             @input="isEpgSearch ? triggerEpgAutocompleteSearch() : null">
      <button aria-label="Suchen"
              class="bx-form__search-submit"
              tabindex="-1"
              @click="submitSearch()"
              @keyup.enter="submitSearch()">
        <Icon name="search" />
      </button>
      <div class="bx-form__search-tabs-wrapper">
        <div class="bx-form__search-tabs">
          <button v-for="type in searchTypes"
                  :key="type.value"
                  role="radio"
                  :aria-label="type.placeholder"
                  :aria-checked="searchType === type.value"
                  :data-search-type="type.value"
                  type="button"
                  :class="['bx-form__search-tab bx-typo__search-tab', { 'is-active': searchType === type.value }]"
                  @click.prevent="searchType = type.value,
                                  setFocus(),
                                  isEpgSearch && searchPhrase ? triggerEpgAutocompleteSearch() : null">
            {{ type.label }}
          </button>
        </div>
      </div>

      <button aria-label="Suche schließen"
              type="button"
              tabindex="-1"
              class="bx-header__search-close"
              @click="toggleSearch()"
              @keyup.enter="toggleSearch()">
        <Icon name="close" />
      </button>

      <EpgAutocompleteFlyout v-if="isEpgSearch && searchPhrase"
                             :search-phrase="searchPhrase"
                             :epg-autocomplete-search="epgAutocompleteSearch"
                             :has-api-error="hasApiError"
                             :search-in-progress="searchInProgress"
                             @close-flyout="cleanupEpgAutocompleteSearch()" />
    </form>
  </div>
</template>

<script>
import { mapState } from 'pinia'

import { useConfigStore } from '../../../stores/config'
import { cleanUpSearchPhrase } from '../../../utils/strings.ts'
import EpgAutocompleteFlyout from './EpgSearch/EpgAutocompleteFlyout.vue'
import Icon from '../../../components/shared/Icon.vue'
import { useKeepTabbingFocus } from '../../../composables/use-keep-tabbing-focus'
import { getAxios } from '../../../api/axios'

export default {
  components: {
    EpgAutocompleteFlyout,
    Icon
  },
  setup () {
    return useKeepTabbingFocus()
  },
  data () {
    return {
      hasApiError: false,
      searchInProgress: false,
      searchPhrase: '',
      showSearch: false,
      searchType: 'epg', // ToDo: Prevent fallback to default after submiting search
      searchTypes: [
        {
          value: 'epg',
          label: 'TV Programm',
          placeholder: 'Sender und Sendungen suchen'
        },
        {
          value: 'wer-streamt',
          label: 'Streaming',
          placeholder: 'Nach Streaming-Titeln suchen'
        },
        {
          value: 'suche',
          label: 'News',
          placeholder: 'Nach TV-News suchen'
        }
      ],
      epgAutocompleteSearch: {
        channels: [],
        broadcasts: {} // see `EpgAutocompleteSearchResult` type
      },
      debounceTimeoutId: null
    }
  },
  computed: {
    ...mapState(useConfigStore, ['rsConfig']),
    placeholder () {
      return this.searchTypes.find(type => type.value === this.searchType)?.placeholder
    },
    isEpgSearch () {
      return this.searchType === 'epg'
    }
  },
  watch: {
    epgAutocompleteSearch: {
      handler () {
        this.keepTabbingFocusInHeaderSearch()
      },
      deep: true
    }
  },
  methods: {
    cleanupEpgAutocompleteSearch () {
      this.searchPhrase = ''
      this.resetEpgAutocompleteSearchResults()
      if (this.showSearch) {
        this.toggleSearch()
      }
    },
    toggleSearch () {
      this.showSearch = document.body.classList.toggle('bx-js-show-search')
      if (this.showSearch) {
        this.setFocus()
        this.keepTabbingFocusInHeaderSearch()
      }
    },
    setFocus () {
      this.$refs.searchInput.focus()
    },
    submitSearch () {
      if (!this.isEpgSearch) {
        const url = `/${this.searchType}?query=${encodeURIComponent(this.$refs.searchInput.value.trim())}`
        if (this.searchType === 'wer-streamt') {
          window.location.href = `${url}#search-filters`
        } else {
          window.location.href = `${url}`
        }
      }
    },
    triggerEpgAutocompleteSearch () {
      if (this.searchPhrase) {
        clearTimeout(this.debounceTimeoutId)
        this.debounceTimeoutId = setTimeout(this.performEpgAutocompleteSearch, 250) // be kind to the server
      } else {
        this.resetEpgAutocompleteSearchResults()
      }
    },
    async performEpgAutocompleteSearch () {
      this.searchInProgress = true
      this.hasApiError = false

      try {
        const axios = await getAxios()
        const url = this.rsConfig.epgAutocompleteSearchServiceUrl
        const params = { phrase: cleanUpSearchPhrase(this.searchPhrase) } // no schmutz in composer
        const { data } = await axios.get(url, { params }) || {}

        this.epgAutocompleteSearch = {
          channels: data.channels || [],
          broadcasts: data.broadcasts || {}
        }
      } catch (error) {
        this.epgAutocompleteSearch = {
          channels: [],
          broadcasts: {}
        }
        this.hasApiError = true
        console.error('Error fetching EPG autocomplete search results:', error)
      }
      this.searchInProgress = false
    },
    resetEpgAutocompleteSearchResults () {
      this.epgAutocompleteSearch = {
        channels: [],
        broadcasts: {}
      }
    },
    keepTabbingFocusInHeaderSearch () {
      this.$nextTick(() => {
        this.removeTabbingEventListeners()
        this.keepTabbingFocus(this.$refs.headerSearchForm, ['input, button:not(.bx-header__search-close):not(.bx-form__search-submit), .bx-epg-search--autocomplete a'], 0, () => {
          this.removeTabbingEventListeners()
          this.toggleSearch()
          this.$refs.headerSearchOpener.focus()
        })
      })
    }
  }
}
</script>
